/**
 * Parser de archivos CSV/XLSX de CUIL + sueldo para la carga masiva de la DDJJ
 * — Ticket Mantis #496.
 *
 * Devuelve las filas válidas (CUIL normalizado + sueldo numérico) y una lista
 * de errores por fila. El matcheo contra la nómina y el resumen se hacen en el
 * componente; acá solo parseamos y validamos el archivo.
 */

import ExcelJS from 'exceljs';
import { cuitDigits } from './format';

export interface FilaImportada {
  /** CUIL normalizado a 11 dígitos. */
  cuilDigits: string;
  sueldo: number;
  /** Nº de fila en el archivo (1-based, con encabezado) para reportar errores. */
  fila: number;
}

export interface ErrorImport {
  fila: number;
  motivo: string;
}

export interface ParseResult {
  filas: FilaImportada[];
  errores: ErrorImport[];
}

export interface ParseOpts {
  /** Tope de sueldo (defensa anti-overflow, alineado con #031). */
  maxSueldo?: number;
}

const CUIL_HEADERS = ['cuil', 'cuit'];
const SUELDO_HEADERS = ['sueldo', 'remuneracion', 'salario', 'monto', 'haberes'];

function normalizarHeader(h: string): string {
  return h.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
}

/**
 * Convierte un sueldo escrito de varias formas a número:
 * "$ 1.234,56" -> 1234.56 · "1234.56" -> 1234.56 · "1500" -> 1500.
 * Devuelve null si no es un número válido.
 */
export function parseSueldo(raw: unknown): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === '') return null;
  s = s.replace(/\$/g, '').replace(/\s/g, '');
  if (s.includes(',') && s.includes('.')) {
    // formato es-AR: punto = miles, coma = decimal
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function detectarColumnas(header: string[]): { cuilIdx: number; sueldoIdx: number } | null {
  const norm = header.map(normalizarHeader);
  const cuilIdx = norm.findIndex((h) => CUIL_HEADERS.includes(h));
  const sueldoIdx = norm.findIndex((h) => SUELDO_HEADERS.includes(h));
  if (cuilIdx === -1 || sueldoIdx === -1) return null;
  return { cuilIdx, sueldoIdx };
}

/**
 * Procesa una matriz (filas x celdas). La 1ª fila es el encabezado.
 */
export function procesarFilas(matriz: string[][], opts: ParseOpts = {}): ParseResult {
  const maxSueldo = opts.maxSueldo ?? Infinity;
  const errores: ErrorImport[] = [];
  const filas: FilaImportada[] = [];

  if (matriz.length === 0) {
    return { filas, errores: [{ fila: 0, motivo: 'El archivo está vacío.' }] };
  }

  const cols = detectarColumnas(matriz[0]);
  if (!cols) {
    return {
      filas,
      errores: [{ fila: 1, motivo: 'No se encontraron las columnas "cuil" y "sueldo" en el encabezado.' }],
    };
  }

  for (let i = 1; i < matriz.length; i++) {
    const row = matriz[i];
    const filaNum = i + 1;
    const cuilRaw = (row[cols.cuilIdx] ?? '').toString();
    const sueldoRaw = (row[cols.sueldoIdx] ?? '').toString();

    // Fila totalmente vacía: se ignora sin error.
    if (cuilRaw.trim() === '' && sueldoRaw.trim() === '') continue;

    const cuilDigits = cuitDigits(cuilRaw);
    if (cuilDigits.length !== 11) {
      errores.push({ fila: filaNum, motivo: `CUIL inválido: "${cuilRaw}"` });
      continue;
    }
    const sueldo = parseSueldo(sueldoRaw);
    if (sueldo === null) {
      errores.push({ fila: filaNum, motivo: `Sueldo inválido: "${sueldoRaw}"` });
      continue;
    }
    if (sueldo < 0) {
      errores.push({ fila: filaNum, motivo: `Sueldo negativo: "${sueldoRaw}"` });
      continue;
    }
    if (sueldo > maxSueldo) {
      errores.push({ fila: filaNum, motivo: `Sueldo demasiado grande: "${sueldoRaw}"` });
      continue;
    }
    filas.push({ cuilDigits, sueldo, fila: filaNum });
  }

  return { filas, errores };
}

// ── CSV ─────────────────────────────────────────────────────────────────────

function detectarDelim(headerLine: string): string {
  const coma = (headerLine.match(/,/g) ?? []).length;
  const puntoComa = (headerLine.match(/;/g) ?? []).length;
  const tab = (headerLine.match(/\t/g) ?? []).length;
  if (puntoComa >= coma && puntoComa >= tab && puntoComa > 0) return ';';
  if (tab > coma && tab > puntoComa) return '\t';
  return ',';
}

function splitCsvLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else { inQ = !inQ; }
    } else if (c === delim && !inQ) {
      out.push(cur); cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

export function parseCsvTexto(texto: string): string[][] {
  const lineas = texto
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');
  if (lineas.length === 0) return [];
  const delim = detectarDelim(lineas[0]);
  return lineas.map((l) => splitCsvLine(l, delim));
}

// ── XLSX ────────────────────────────────────────────────────────────────────

export async function parseXlsxArrayBuffer(buf: ArrayBuffer): Promise<string[][]> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  if (!ws) return [];
  const matriz: string[][] = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    const vals: string[] = [];
    row.eachCell({ includeEmpty: true }, (cell) => {
      vals.push(cell.value == null ? '' : String(cell.text ?? cell.value));
    });
    matriz.push(vals);
  });
  return matriz;
}

// ── Entrada principal ─────────────────────────────────────────────────────────

export async function parseArchivoSueldos(file: File, opts: ParseOpts = {}): Promise<ParseResult> {
  const nombre = file.name.toLowerCase();
  let matriz: string[][];
  if (nombre.endsWith('.csv')) {
    matriz = parseCsvTexto(await file.text());
  } else if (nombre.endsWith('.xlsx')) {
    matriz = await parseXlsxArrayBuffer(await file.arrayBuffer());
  } else {
    return { filas: [], errores: [{ fila: 0, motivo: 'Formato no soportado. Usá un archivo .csv o .xlsx.' }] };
  }
  return procesarFilas(matriz, opts);
}
